import React, { useEffect, useRef, useState } from 'react';
import { Template1ResumeData, flattenSkills, filterNullEntries } from './Template1';
import { ResumePaginator, PageMargins } from '@/utils/resumePagination';

interface PaginatedTemplate3Props {
  data: Template1ResumeData;
  margins?: PageMargins;
}

export const PaginatedTemplate3: React.FC<PaginatedTemplate3Props> = ({ data, margins }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paginatedContent, setPaginatedContent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const sections: HTMLElement[] = [];

    // Header Section - Executive Style
    const header = document.createElement('header');
    header.className = 'bg-purple-900 text-white p-4 mb-3 -mx-12 -mt-12 mb-3';
    header.setAttribute('data-keep-together', 'true');
    header.innerHTML = `
      <h1 class="text-3xl font-bold mb-1">${data.Name}</h1>
      <div class="text-sm">
        ${data.Email} | ${data.Phone} | ${data.City}, ${data.State}
        ${data.LinkedIn ? ` | ${data.LinkedIn}` : ''}
      </div>
    `;
    sections.push(header);

    // Summary Section
    const summary = document.createElement('section');
    summary.className = 'mb-3';
    summary.innerHTML = `
      <div class="border-l-4 border-purple-600 pl-3">
        <h2 class="text-base font-bold text-purple-900 mb-2">EXECUTIVE SUMMARY</h2>
        <p class="text-xs text-gray-800 leading-snug">${data.Summary}</p>
      </div>
    `;
    sections.push(summary);

    // Skills Section - Support both SkillRows and Skills formats
    const allSkills = flattenSkills(data);
    if (allSkills.length > 0) {
      const skills = document.createElement('section');
      skills.className = 'mb-3';
      const skillsGrid = allSkills.map(s => `<span class="bg-purple-100 text-purple-900 px-2 py-1 rounded text-xs">${s}</span>`).join(' ');
      skills.innerHTML = `
        <div class="border-l-4 border-purple-600 pl-3">
          <h2 class="text-base font-bold text-purple-900 mb-2">CORE COMPETENCIES</h2>
          <div class="flex flex-wrap gap-1">${skillsGrid}</div>
        </div>
      `;
      sections.push(skills);
    }

    // Career Highlights
    if (data.CareerHighlights && data.CareerHighlights.length > 0) {
      const highlights = document.createElement('section');
      highlights.className = 'mb-3';
      const highlightsHtml = data.CareerHighlights.map(h => `
        <div class="mb-2 flex items-start gap-2">
          <span class="text-purple-600 font-bold mt-0.5">●</span>
          <p class="text-xs text-gray-800 leading-snug">
            <span class="font-bold">${h.chtitle}:</span> ${h.chdescription}
          </p>
        </div>
      `).join('');
      highlights.innerHTML = `
        <div class="border-l-4 border-purple-600 pl-3">
          <h2 class="text-base font-bold text-purple-900 mb-2">CAREER HIGHLIGHTS</h2>
          ${highlightsHtml}
        </div>
      `;
      sections.push(highlights);
    }

    // Professional Experience with Timeline
    if (data.ProfessionalExperience && data.ProfessionalExperience.length > 0) {
      const firstJob = data.ProfessionalExperience[0];
      const expBlock = document.createElement('section');
      expBlock.className = 'mb-3';
      expBlock.setAttribute('data-keep-together', 'true');

      const firstBullets = firstJob.Descriptions.map(desc => 
        `<li class="leading-snug text-xs text-gray-800">${desc.Description}</li>`
      ).join('');

      expBlock.innerHTML = `
        <div class="border-l-4 border-purple-600 pl-3">
          <h2 class="text-base font-bold text-purple-900 mb-3">PROFESSIONAL EXPERIENCE</h2>
          <div class="mb-3 relative">
            <div class="absolute -left-5 w-3 h-3 bg-purple-600 rounded-full"></div>
            <div class="flex justify-between items-baseline mb-1">
              <h3 class="font-bold text-sm text-gray-900">${firstJob.company}</h3>
              <span class="text-xs text-gray-600 bg-purple-50 px-2 py-0.5 rounded">${firstJob.start_date} - ${firstJob.end_date}</span>
            </div>
            <p class="text-xs font-semibold text-purple-800 mb-1">${firstJob.job_title}</p>
            ${firstJob.location ? `<p class="text-xs text-gray-600 mb-1">${firstJob.location}</p>` : ''}
            <ul class="list-disc list-inside ml-2 space-y-0.5">${firstBullets}</ul>
          </div>
        </div>
      `;
      sections.push(expBlock);

      data.ProfessionalExperience.slice(1).forEach((job) => {
        const jobSection = document.createElement('section');
        jobSection.className = 'mb-3';
        jobSection.setAttribute('data-experience-continuation', 'true');
        
        const bullets = job.Descriptions.map(desc => 
          `<li class="leading-snug text-xs text-gray-800">${desc.Description}</li>`
        ).join('');
        
        jobSection.innerHTML = `
          <div class="border-l-4 border-purple-600 pl-3">
            <div class="mb-3 relative">
              <div class="absolute -left-5 w-3 h-3 bg-purple-600 rounded-full"></div>
              <div class="flex justify-between items-baseline mb-1" data-stick-with-next-list="true">
                <h3 class="font-bold text-sm text-gray-900">${job.company}</h3>
                <span class="text-xs text-gray-600 bg-purple-50 px-2 py-0.5 rounded">${job.start_date} - ${job.end_date}</span>
              </div>
              <p class="text-xs font-semibold text-purple-800 mb-1" data-stick-with-next-list="true">${job.job_title}</p>
              ${job.location ? `<p class="text-xs text-gray-600 mb-1" data-stick-with-next-list="true">${job.location}</p>` : ''}
              <ul class="list-disc list-inside ml-2 space-y-0.5">${bullets}</ul>
            </div>
          </div>
        `;
        sections.push(jobSection);
      });
    }

    // Education Section
    const education = document.createElement('section');
    education.className = 'mb-3';
    const eduHtml = data.Education.map(edu => `
      <div class="mb-2">
        <p class="text-xs font-bold text-gray-900">${edu.College_Name}</p>
        <p class="text-xs text-gray-800">${edu.College_Degree_Name}${edu.Major ? `, ${edu.Major}` : ''}</p>
        ${(edu.Year_Earned || edu.YearEarned) ? `<p class="text-xs text-gray-600">${edu.Year_Earned || edu.YearEarned}</p>` : ''}
      </div>
    `).join('');
    education.innerHTML = `
      <div class="border-l-4 border-purple-600 pl-3">
        <h2 class="text-base font-bold text-purple-900 mb-2">EDUCATION</h2>
        ${eduHtml}
      </div>
    `;
    sections.push(education);

    // Certifications - Filter out null entries
    const validCertifications = filterNullEntries(data.CertificationsAndLicenses);
    if (validCertifications.length > 0) {
      const certs = document.createElement('section');
      certs.className = 'mb-3';
      const certsHtml = validCertifications.map(cert => `
        <p class="text-xs text-gray-800 mb-1">
          <span class="font-semibold">${cert.Certification_or_License_Name}</span>
          ${cert.Institution_Organization ? ` - ${cert.Institution_Organization}` : ''}
          ${cert.Year ? ` (${cert.Year})` : ''}
        </p>
      `).join('');
      certs.innerHTML = `
        <div class="border-l-4 border-purple-600 pl-3">
          <h2 class="text-base font-bold text-purple-900 mb-2">CERTIFICATIONS</h2>
          ${certsHtml}
        </div>
      `;
      sections.push(certs);
    }

    const paginator = new ResumePaginator({
      margins: margins || { top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }
    });

    const tempContainer = document.createElement('div');
    const paginated = paginator.paginate(tempContainer, sections);
    setPaginatedContent(paginated);

  }, [data, margins]);

  return (
    <div ref={containerRef}>
      {paginatedContent && (
        <div 
          className="font-sans"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
          dangerouslySetInnerHTML={{ __html: paginatedContent.innerHTML }} 
        />
      )}
    </div>
  );
};
